/**
 * Resolves Ninja Forms {querystring:KEY} merge tags in the browser.
 *
 * Forms are lazy-loaded via REST, so the server never sees the page query string
 * and replaces these tags with an empty string. The raw field default (with the tag)
 * is still available on the field model, so the value is rebuilt here. AB#254796
 *
 * Only hidden fields are handled: their template escapes the value, so URL values are kept
 * as-is. Other templates (e.g. html, password) print the value unescaped and must not
 * receive raw URL input.
 */
const $ = jQuery;

const TAG_REGEX = /{querystring:([^}]+)}/g;
const OTHER_TAG_REGEX = /{[^}]+:[^}]+}/;
const MAX_LENGTH = 500;

export const cleanValue = value => (value || '').trim().substring(0, MAX_LENGTH);

/**
 * @function resolveDefault
 * @description Returns the resolved value, or null when the default has no querystring tags
 * or still contains other merge tags that only the server can resolve.
 */
export const resolveDefault = (rawDefault, search) => {
	if (typeof rawDefault !== 'string' || !rawDefault.match(TAG_REGEX)) {
		return null;
	}

	const params = new URLSearchParams(search);
	const resolved = rawDefault.replace(TAG_REGEX, (match, key) => cleanValue(params.get(key)));

	return OTHER_TAG_REGEX.test(resolved) ? null : resolved.trim();
};

/**
 * @function applyToForm
 * @description Sets resolved querystring values on the form's hidden fields.
 *
 * SECURITY: only hidden fields are processed, on purpose. Values come straight from the URL and are
 * NOT stripped of dangerous characters (see cleanValue). That is safe only because the hidden field
 * template escapes the value: value="{{{ _.escape( data.value ) }}}".
 *
 * Other Ninja Forms templates print the value unescaped, e.g. fields-html.html ({{{ data.value }}}),
 * fields-password.html, fields-color.html and the product quantity input. Passing raw URL input to
 * them allows reflected XSS, e.g. ?x=<img src=x onerror=alert(1)>.
 *
 * Before adding another field type here, check its template in ninja-forms/includes/Templates/.
 * If the template does not escape the value, escape or sanitize the value for that type first.
 */
export const applyToForm = (formModel, search) => {
	formModel.get('fields').each((field) => {
		if (field.get('type') !== 'hidden') {
			return;
		}

		const value = resolveDefault(field.get('default'), search);
		if (value !== null) {
			field.set('value', value);
		}
	});
};

const init = () => {
	$(document).on('nfFormReady', (e, layoutView) => applyToForm(layoutView.model, window.location.search));
};

export default init;



// WEBPACK FOOTER //
// ./wp-content/plugins/core/assets/theme/js/src/forms/nf-querystring-merge-tags.js